import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import { DateRangePicker, FocusedInputShape } from "react-dates";
import moment, { Moment } from "moment";
import "moment-timezone";
import "react-dates/lib/css/_datepicker.css";
import styles from "./index.module.css";

interface DateSelectorProps {
  onDateChange: (dates: {
    startDate: Moment | null;
    endDate: Moment | null;
    selectedOption: string;
  }) => void;
  initialStartDate: Moment | null;
  initialEndDate: Moment | null;
  initialSelectedOption: string;
  onClose?: () => void;
}

const DateSelector: React.FC<DateSelectorProps> = ({
  onDateChange,
  initialStartDate,
  initialEndDate,
  initialSelectedOption,
  onClose,
}) => {
  const [dateRange, setDateRange] = useState({
    startDate: initialStartDate ? moment(initialStartDate) : null,
    endDate: initialEndDate ? moment(initialEndDate) : null,
  });
  const [focusedInput, setFocusedInput] = useState<FocusedInputShape | null>(
    null
  );
  const [quickSelect, setQuickSelect] = useState(
    initialSelectedOption || "custom"
  );
  const [numberOfMonths] = useState(1);
  const [hoveredEndDate, setHoveredEndDate] = useState<Moment | null>(null);
  const [hoveredDate, setHoveredDate] = useState<Moment | null>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hoverDateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      if (hoverDateTimeoutRef.current) {
        clearTimeout(hoverDateTimeoutRef.current);
      }
    };
  }, []);

  // Keep calendar open and set focus when only one date is selected
  useEffect(() => {
    console.log("useEffect focus manager", { focusedInput, dateRange });
    // If we have start date but no end date, focus on end date
    if (
      dateRange.startDate &&
      !dateRange.endDate &&
      focusedInput !== "endDate"
    ) {
      console.log("useEffect: Setting focus to endDate");
      setFocusedInput("endDate");
    }
    // If we have end date but no start date, focus on start date
    else if (
      dateRange.endDate &&
      !dateRange.startDate &&
      focusedInput !== "startDate"
    ) {
      console.log("useEffect: Setting focus to startDate");
      setFocusedInput("startDate");
    }
  }, [dateRange, focusedInput]);

  const quickSelectOptions = useMemo(
    () => [
      { label: "Today", value: "today" },
      { label: "Last 7 Days", value: "last7days" },
      { label: "Last 30 Days", value: "last30Days" },
      { label: "Last 3 Months", value: "last3Months" },
      { label: "Last 6 Months", value: "last6Months" },
      { label: "Last 12 Months", value: "last12Months" },
      { label: "Custom", value: "custom" },
    ],
    []
  );

  const handleQuickSelect = useCallback(
    (option: string, label: string) => {
      // Always use Africa/Cairo timezone for date calculations
      const cairoTimezone = "Africa/Cairo";
      let start: moment.Moment, end: moment.Moment;

      switch (option) {
        case "today":
          // First second of today to last second of today in Cairo
          start = moment().tz(cairoTimezone).startOf("day");
          end = moment().tz(cairoTimezone).endOf("day");
          break;
        case "last7days":
          // Last 7 days: from the first second of 7 days ago to the current moment
          // Example: If today is Dec 31, start is Dec 24 00:00:00, end is Dec 31 (current time)
          end = moment().tz(cairoTimezone); // Current moment
          start = end.clone().subtract(7, "days").startOf("day"); // First second of 7 days ago
          break;
        case "last30Days":
          // Last 30 days: from the first second of 30 days ago to the current moment
          end = moment().tz(cairoTimezone); // Current moment
          start = end.clone().subtract(30, "days").startOf("day"); // First second of 30 days ago
          break;
        case "last3Months":
          // Last 3 months: from the first second of 3 months ago to the current moment
          end = moment().tz(cairoTimezone); // Current moment
          start = end.clone().subtract(3, "months").startOf("day"); // First second of 3 months ago
          break;
        case "last6Months":
          // Last 6 months: from the first second of 6 months ago to the current moment
          end = moment().tz(cairoTimezone); // Current moment
          start = end.clone().subtract(6, "months").startOf("day"); // First second of 6 months ago
          break;
        case "last12Months":
          // Last 12 months: from the first second of 12 months ago to the current moment
          end = moment().tz(cairoTimezone); // Current moment
          start = end.clone().subtract(12, "months").startOf("day"); // First second of 12 months ago
          break;
        case "custom":
          // For custom, don't auto-set dates - user picks from calendar
          // Set the quickSelect state and open the calendar
          setQuickSelect("custom");
          // Open the calendar by focusing on the start date input
          setFocusedInput("startDate");
          return;
        default:
          start = moment().tz(cairoTimezone).startOf("day");
          end = moment().tz(cairoTimezone).endOf("day");
      }

      // Convert Cairo local time to UTC for API calls
      // This ensures full local days are covered in UTC
      const startUTC = start.clone().utc();
      const endUTC = end.clone().utc();

      setDateRange({ startDate: start, endDate: end });
      setQuickSelect(option);
      onDateChange({
        startDate: startUTC,
        endDate: endUTC,
        selectedOption: label,
      });
    },
    [onDateChange]
  );

  // Handle date changes - STRICT: only send request when BOTH dates are in the current payload
  const handleDateChange = useCallback(
    (dates: { startDate: Moment | null; endDate: Moment | null }) => {
      // Log raw payload as emitted by picker BEFORE any processing or state updates
      console.log("🔵 RAW PAYLOAD from picker (before any processing):", {
        startDate: dates.startDate
          ? dates.startDate.format("YYYY-MM-DD")
          : null,
        endDate: dates.endDate ? dates.endDate.format("YYYY-MM-DD") : null,
        startDateRaw: dates.startDate,
        endDateRaw: dates.endDate,
        hasStart: !!dates.startDate,
        hasEnd: !!dates.endDate,
      });

      console.log("handleDateChange called", {
        dates,
        dateRange,
        focusedInput,
        hasStart: !!dates.startDate,
        hasEnd: !!dates.endDate,
      });
      setQuickSelect("custom");

      // KEY FIX: Only consider endDate if user is currently selecting it
      // This prevents pre-populated endDate from triggering premature completion
      const effectiveEndDate =
        focusedInput === "endDate" ? dates.endDate : null;

      // Compute completeness using effective dates (ignoring pre-populated endDate)
      const hasStart = !!dates.startDate;
      const hasEnd = !!effectiveEndDate;

      console.log("Effective dates calculation:", {
        focusedInput,
        rawEndDate: dates.endDate ? dates.endDate.format("YYYY-MM-DD") : null,
        effectiveEndDate: effectiveEndDate
          ? effectiveEndDate.format("YYYY-MM-DD")
          : null,
        hasStart,
        hasEnd,
      });

      // Case 1: Both dates are provided in the current call - complete the range
      if (hasStart && hasEnd) {
        console.log("Case 1: Both dates in payload - completing range", {
          startDate: dates.startDate?.format("YYYY-MM-DD"),
          effectiveEndDate: effectiveEndDate?.format("YYYY-MM-DD"),
        });
        // TypeScript: We know both are non-null due to hasStart && hasEnd check
        const startDate = dates.startDate!;
        const endDate = effectiveEndDate!;

        // Ensure start date is before or equal to end date
        let finalStart = startDate;
        let finalEnd = endDate;

        // If end date is before start date, swap them
        if (finalEnd.isBefore(finalStart)) {
          [finalStart, finalEnd] = [finalEnd, finalStart];
        }

        setDateRange({ startDate: finalStart, endDate: finalEnd });

        // Clear hover state when range is complete
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current);
          hoverTimeoutRef.current = null;
        }
        if (hoverDateTimeoutRef.current) {
          clearTimeout(hoverDateTimeoutRef.current);
          hoverDateTimeoutRef.current = null;
        }
        setHoveredEndDate(null);
        setHoveredDate(null);

        // Close calendar and send request
        console.log(
          "Case 1: Setting focusedInput to null and calling onDateChange"
        );
        setFocusedInput(null);

        // Convert custom dates to Cairo timezone first, then to UTC
        // This ensures full local days are covered (first second to last second)
        // Example: Selecting Dec 21-24 means:
        // - Start: Dec 21 00:00:00 (first second of Dec 21)
        // - End: Dec 24 23:59:59.999 (last second of Dec 24)
        const cairoTimezone = "Africa/Cairo";
        // The dates from the picker are date-only (no time), interpret them in Cairo timezone
        // Create moment objects in Cairo timezone with the selected dates
        const startCairo = moment.tz(
          `${finalStart.format("YYYY-MM-DD")} 00:00:00`,
          "YYYY-MM-DD HH:mm:ss",
          cairoTimezone
        );
        const endCairo = moment.tz(
          `${finalEnd.format("YYYY-MM-DD")} 23:59:59.999`,
          "YYYY-MM-DD HH:mm:ss.SSS",
          cairoTimezone
        );

        // Convert to UTC for API calls
        onDateChange({
          startDate: startCairo.clone().utc(),
          endDate: endCairo.clone().utc(),
          selectedOption: "Custom",
        });
        return;
      }

      // Case 2: Only start date in current payload - partial selection, keep calendar open
      if (hasStart && !hasEnd) {
        console.log(
          "Case 2: Only start date - storing partial, keeping calendar open"
        );
        // Store partial selection, do NOT complete using old state
        setDateRange({ startDate: dates.startDate, endDate: null });
        // Clear hover state when start date changes
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current);
          hoverTimeoutRef.current = null;
        }
        if (hoverDateTimeoutRef.current) {
          clearTimeout(hoverDateTimeoutRef.current);
          hoverDateTimeoutRef.current = null;
        }
        setHoveredEndDate(null);
        setHoveredDate(null);
        // useEffect will handle setting focus to "endDate"
        return;
      }

      // Case 3: Only end date in current payload - partial selection, keep calendar open
      if (!hasStart && hasEnd) {
        console.log(
          "Case 3: Only end date - storing partial, keeping calendar open"
        );
        // Store partial selection, do NOT complete using old state
        setDateRange({ startDate: null, endDate: effectiveEndDate });
        // Clear hover state when end date changes
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current);
          hoverTimeoutRef.current = null;
        }
        if (hoverDateTimeoutRef.current) {
          clearTimeout(hoverDateTimeoutRef.current);
          hoverDateTimeoutRef.current = null;
        }
        setHoveredEndDate(null);
        setHoveredDate(null);
        // useEffect will handle setting focus to "startDate"
        return;
      }

      // Case 4: Both dates are null (cleared)
      if (!hasStart && !hasEnd) {
        console.log("Case 4: Both dates null - clearing");
        setDateRange({ startDate: null, endDate: null });
        // Clear hover state when dates are cleared
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current);
          hoverTimeoutRef.current = null;
        }
        if (hoverDateTimeoutRef.current) {
          clearTimeout(hoverDateTimeoutRef.current);
          hoverDateTimeoutRef.current = null;
        }
        setHoveredEndDate(null);
        setHoveredDate(null);
        // Keep calendar open, focus on start date
        setFocusedInput("startDate");
        return;
      }
    },
    [onDateChange, focusedInput, dateRange]
  );

  const renderQuickSelectOption = useCallback(
    ({ label, value }: { label: string; value: string }) => (
      <li
        key={value}
        className={`${styles.quickSelectOption} ${
          quickSelect === value ? styles.selected : ""
        }`}
        onClick={() => handleQuickSelect(value, label)}
      >
        {label}
        {value === "custom" && (
          <svg
            className={styles.calendarIconOption}
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12.6667 2.66667H12V2C12 1.63181 11.7015 1.33333 11.3333 1.33333C10.9651 1.33333 10.6667 1.63181 10.6667 2V2.66667H5.33333V2C5.33333 1.63181 5.03486 1.33333 4.66667 1.33333C4.29848 1.33333 4 1.63181 4 2V2.66667H3.33333C2.59695 2.66667 2 3.26362 2 4V13.3333C2 14.0697 2.59695 14.6667 3.33333 14.6667H12.6667C13.403 14.6667 14 14.0697 14 13.3333V4C14 3.26362 13.403 2.66667 12.6667 2.66667ZM12.6667 13.3333H3.33333V6H12.6667V13.3333Z"
              fill="#4A90E2"
            />
          </svg>
        )}
      </li>
    ),
    [quickSelect, handleQuickSelect]
  );

  // Determine if a date is in the hover range (between start and hovered end)
  const isInHoverRange = useCallback(
    (day: Moment): boolean => {
      // Only show hover range if we have start date, hovered end, but no actual end date
      if (!dateRange.startDate || !hoveredEndDate || dateRange.endDate)
        return false;

      const start = dateRange.startDate.startOf("day");
      const end = hoveredEndDate.startOf("day");
      const dayStart = day.startOf("day");

      // Handle both directions (start before end, or end before start)
      if (end.isAfter(start)) {
        return dayStart.isAfter(start) && dayStart.isBefore(end);
      } else if (end.isBefore(start)) {
        return dayStart.isAfter(end) && dayStart.isBefore(start);
      }
      return false;
    },
    [dateRange.startDate, dateRange.endDate, hoveredEndDate]
  );

  // Determine if a date is the hovered end date
  const isHoveredEndDate = useCallback(
    (day: Moment): boolean => {
      // Only show hovered end if we don't have an actual end date
      if (!hoveredEndDate || dateRange.endDate) return false;
      return day.startOf("day").isSame(hoveredEndDate.startOf("day"));
    },
    [hoveredEndDate, dateRange.endDate]
  );

  // Determine if a date is currently hovered
  const isHoveredDate = useCallback(
    (day: Moment): boolean => {
      if (!hoveredDate) return false;
      return day.startOf("day").isSame(hoveredDate.startOf("day"));
    },
    [hoveredDate]
  );

  // Custom day className for dynamic styling
  const dayClassName = useCallback(
    (day: Moment): string => {
      const dayStart = day.startOf("day");
      const isStartDate =
        dateRange.startDate?.startOf("day").isSame(dayStart) || false;
      const isEndDate =
        dateRange.endDate?.startOf("day").isSame(dayStart) || false;
      const isHovered = isHoveredDate(day);

      // Clicked dates always take priority
      if (isStartDate || isEndDate) {
        return styles.highlightedDay;
      }

      // Check hover states only if no end date is selected
      const isInHover = isInHoverRange(day);
      const isHoveredEnd = isHoveredEndDate(day);

      // Days in actual selected range (between start and end)
      const isInSelectedRange =
        dateRange.startDate &&
        dateRange.endDate &&
        dayStart.isAfter(dateRange.startDate.startOf("day")) &&
        dayStart.isBefore(dateRange.endDate.startOf("day"));

      const classes: string[] = [];

      // Apply highlight to hovered date OR hovered end date (same styling as start date)
      // Hovered end date should show #005073 with circular border-radius just like start date
      if (isHovered || isHoveredEnd) {
        classes.push(styles.highlightedDay);
      }

      // Apply range styling to days in selected range or hover range
      if (isInSelectedRange || isInHover) {
        classes.push(styles.inRangeDay);
      }

      return classes.join(" ");
    },
    [
      dateRange.startDate,
      dateRange.endDate,
      isInHoverRange,
      isHoveredEndDate,
      isHoveredDate,
    ]
  );

  // Custom day renderer with hover support
  const renderDayContents = useCallback(
    (day: Moment) => {
      const dayStart = day.startOf("day");
      const isStartDate =
        dateRange.startDate?.startOf("day").isSame(dayStart) || false;
      const isEndDate =
        dateRange.endDate?.startOf("day").isSame(dayStart) || false;

      return (
        <div
          className={styles.customDay}
          onMouseEnter={() => {
            // Clear any pending timeouts
            if (hoverTimeoutRef.current) {
              clearTimeout(hoverTimeoutRef.current);
              hoverTimeoutRef.current = null;
            }
            if (hoverDateTimeoutRef.current) {
              clearTimeout(hoverDateTimeoutRef.current);
              hoverDateTimeoutRef.current = null;
            }

            // Always track individual date hover (for general hover styling)
            setHoveredDate(day);

            // Track hovered end date for range preview and styling if:
            // 1. We have a start date
            // 2. No end date is selected yet
            // 3. We're focused on selecting the end date
            // 4. This day is not the start date
            // This ensures hovered end date gets the same styling as start date
            if (
              dateRange.startDate &&
              !dateRange.endDate &&
              focusedInput === "endDate" &&
              !isStartDate
            ) {
              setHoveredEndDate(day);
            }
          }}
          onTouchStart={(e) => {
            // Support touch devices (mobile/tablet) - same behavior as mouse hover
            e.stopPropagation();
            // Clear any pending timeouts
            if (hoverTimeoutRef.current) {
              clearTimeout(hoverTimeoutRef.current);
              hoverTimeoutRef.current = null;
            }
            if (hoverDateTimeoutRef.current) {
              clearTimeout(hoverDateTimeoutRef.current);
              hoverDateTimeoutRef.current = null;
            }

            // Always track individual date hover (for general hover styling)
            setHoveredDate(day);

            // Track hovered end date for range preview and styling if:
            // 1. We have a start date
            // 2. No end date is selected yet
            // 3. We're focused on selecting the end date
            // 4. This day is not the start date
            // This ensures hovered end date gets the same styling as start date
            if (
              dateRange.startDate &&
              !dateRange.endDate &&
              focusedInput === "endDate" &&
              !isStartDate
            ) {
              setHoveredEndDate(day);
            }
          }}
          onMouseLeave={() => {
            // Delay clearing hover to prevent flicker when moving between days
            // Only clear if we're not clicking (start/end dates are not clicked)
            if (!isStartDate && !isEndDate) {
              hoverDateTimeoutRef.current = setTimeout(() => {
                setHoveredDate(null);
                hoverDateTimeoutRef.current = null;
              }, 50);

              hoverTimeoutRef.current = setTimeout(() => {
                setHoveredEndDate(null);
                hoverTimeoutRef.current = null;
              }, 50);
            }
          }}
          onTouchEnd={(e) => {
            e.stopPropagation();
            // Don't clear hover immediately on touch end - keep it visible until next touch
            // This provides better UX on mobile/tablet
            // Only clear if we're not clicking (start/end dates are not clicked)
            if (!isStartDate && !isEndDate) {
              // Use a longer timeout for touch devices to keep hover visible
              hoverDateTimeoutRef.current = setTimeout(() => {
                setHoveredDate(null);
                hoverDateTimeoutRef.current = null;
              }, 300);

              hoverTimeoutRef.current = setTimeout(() => {
                setHoveredEndDate(null);
                hoverTimeoutRef.current = null;
              }, 300);
            }
          }}
          onTouchMove={(e) => {
            // Track touch move to update hover as user drags finger
            e.stopPropagation();
            // Clear any pending timeouts
            if (hoverTimeoutRef.current) {
              clearTimeout(hoverTimeoutRef.current);
              hoverTimeoutRef.current = null;
            }
            if (hoverDateTimeoutRef.current) {
              clearTimeout(hoverDateTimeoutRef.current);
              hoverDateTimeoutRef.current = null;
            }

            // Update hover state as finger moves
            setHoveredDate(day);

            if (
              dateRange.startDate &&
              !dateRange.endDate &&
              focusedInput === "endDate" &&
              !isStartDate
            ) {
              setHoveredEndDate(day);
            }
          }}
        >
          {day.date()}
        </div>
      );
    },
    [dateRange.startDate, dateRange.endDate, focusedInput]
  );

  const handleFocusChange = useCallback(
    (input: FocusedInputShape | null) => {
      console.log("onFocusChange called", {
        input,
        dateRange,
        focusedInput,
      });
      // STRICT: Prevent closing calendar unless both dates are selected
      const hasOnlyStartDate =
        dateRange.startDate && !dateRange.endDate;
      const hasOnlyEndDate = !dateRange.startDate && dateRange.endDate;
      const hasBothDates = dateRange.startDate && dateRange.endDate;
      const hasNoDates = !dateRange.startDate && !dateRange.endDate;

      // If trying to close (input === null) and we don't have both dates, prevent it
      if (input === null) {
        console.log(
          "onFocusChange: Attempting to close (input === null)",
          {
            hasOnlyStartDate,
            hasOnlyEndDate,
            hasBothDates,
            hasNoDates,
          }
        );
        if (hasOnlyStartDate) {
          console.log(
            "onFocusChange: Blocked close - only start date, keeping open"
          );
          setFocusedInput("endDate");
          return;
        }
        if (hasOnlyEndDate) {
          console.log(
            "onFocusChange: Blocked close - only end date, keeping open"
          );
          setFocusedInput("startDate");
          return;
        }
        if (hasNoDates) {
          console.log(
            "onFocusChange: Blocked close - no dates, keeping open"
          );
          setFocusedInput("startDate");
          return;
        }
        if (hasBothDates) {
          console.log(
            "onFocusChange: Allowing close - both dates selected"
          );
          if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
          }
          if (hoverDateTimeoutRef.current) {
            clearTimeout(hoverDateTimeoutRef.current);
            hoverDateTimeoutRef.current = null;
          }
          setHoveredEndDate(null);
          setHoveredDate(null);
          setFocusedInput(null);
          return;
        }
      }

      console.log("onFocusChange: Setting focus to", input);
      setFocusedInput(input);
    },
    [dateRange, focusedInput]
  );

  return (
    <div
      className={`${styles.dateSelector} ${
        quickSelect === "custom" ? styles.customMode : ""
      }`}
      onClick={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
    >
      {quickSelect === "custom" ? (
        <DateRangePicker
          startDate={dateRange.startDate}
          startDateId="start_date_id"
          endDate={dateRange.endDate}
          endDateId="end_date_id"
          onDatesChange={handleDateChange}
          focusedInput={focusedInput}
          onFocusChange={handleFocusChange}
          isOutsideRange={() => false}
          showClearDates
          small
          numberOfMonths={numberOfMonths}
          renderDayContents={renderDayContents}
          dayClassName={dayClassName}
        />
      ) : (
        <ul className={styles.quickSelectContainer}>
          {quickSelectOptions.map(renderQuickSelectOption)}
        </ul>
      )}
    </div>
  );
};

export default React.memo(DateSelector);
